/**
 * DifficultySlider - Slider control for AI difficulty (1-10)
 * Debounces onChange to prevent spamming the server while dragging
 */

import { memo, useCallback, useState, useEffect, useRef } from 'react';
import clsx from 'clsx';

interface DifficultySliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  compact?: boolean;
  /** Debounce delay in ms (default: 150) */
  debounceMs?: number;
}

const difficultyLabels: Record<number, string> = {
  1: 'Beginner',
  2: 'Novice',
  3: 'Easy',
  4: 'Medium',
  5: 'Average',
  6: 'Moderate',
  7: 'Hard',
  8: 'Expert',
  9: 'Master',
  10: 'Genius',
};

const getDifficultyColor = (level: number): string => {
  if (level <= 3) return 'text-green-600 dark:text-green-400';
  if (level <= 6) return 'text-yellow-600 dark:text-yellow-400';
  if (level <= 8) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
};

export const DifficultySlider = memo(function DifficultySlider({
  value,
  onChange,
  disabled = false,
  compact = false,
  debounceMs = 150,
}: DifficultySliderProps) {
  // Local state for responsive UI while debouncing
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef(value);

  // Sync local value when prop changes (e.g., from server update)
  useEffect(() => {
    setLocalValue(value);
    lastSentRef.current = value;
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(e.target.value, 10);
      setLocalValue(newValue);

      // Clear pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce the onChange call to prevent spamming
      debounceRef.current = setTimeout(() => {
        // Only send if value changed from last sent
        if (newValue !== lastSentRef.current) {
          lastSentRef.current = newValue;
          onChange(newValue);
        }
      }, debounceMs);
    },
    [onChange, debounceMs]
  );

  const percentage = ((localValue - 1) / 9) * 100;

  const fillColor =
    localValue <= 3 ? '#22c55e' : localValue <= 6 ? '#eab308' : localValue <= 8 ? '#f97316' : '#ef4444';

  return (
    <div className={clsx('w-full', compact ? 'space-y-1' : 'space-y-2')}>
      <div className="flex items-center justify-between">
        <span
          className={clsx(
            'font-medium',
            compact ? 'text-xs' : 'text-sm',
            getDifficultyColor(localValue)
          )}
        >
          {difficultyLabels[localValue]}
        </span>
        <span
          className={clsx(
            'font-mono font-bold',
            compact ? 'text-xs' : 'text-sm',
            getDifficultyColor(localValue)
          )}
        >
          {localValue}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min="1"
          max="10"
          value={localValue}
          onChange={handleChange}
          disabled={disabled}
          className={clsx(
            'difficulty-range w-full appearance-none bg-transparent cursor-pointer h-6',
            disabled && 'opacity-50 cursor-not-allowed',
            '[&::-webkit-slider-runnable-track]:rounded-full',
            compact
              ? '[&::-webkit-slider-runnable-track]:h-1.5'
              : '[&::-webkit-slider-runnable-track]:h-2',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:bg-white',
            '[&::-webkit-slider-thumb]:border-2',
            '[&::-webkit-slider-thumb]:border-slate-400',
            compact
              ? '[&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3'
              : '[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4',
            '[&::-webkit-slider-thumb]:rounded-full',
            compact
              ? '[&::-webkit-slider-thumb]:-mt-[3px]'
              : '[&::-webkit-slider-thumb]:-mt-1',
            '[&::-webkit-slider-thumb]:shadow-md',
            '[&::-webkit-slider-thumb]:transition-transform',
            '[&::-webkit-slider-thumb]:hover:scale-110',
            '[&::-moz-range-track]:rounded-full',
            compact ? '[&::-moz-range-track]:h-1.5' : '[&::-moz-range-track]:h-2',
            '[&::-moz-range-thumb]:appearance-none',
            '[&::-moz-range-thumb]:bg-white',
            '[&::-moz-range-thumb]:border-2',
            '[&::-moz-range-thumb]:border-slate-400',
            compact
              ? '[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3'
              : '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4',
            '[&::-moz-range-thumb]:rounded-full',
            '[&::-moz-range-thumb]:shadow-md'
          )}
          style={{
            ['--fill' as string]: fillColor,
            ['--pct' as string]: `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
});
